// ============================================
// FILE: src/components/FashionCategoryScreen.jsx
// ============================================
//
// Prop-compatible with CategoryProductsScreen, so App.js switches between them
// with a single conditional and nothing else has to know which one is mounted.

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ArrowLeft, Search, SlidersHorizontal, ArrowUpDown, ShoppingBag, X, ChevronDown } from 'lucide-react';
import FashionProductCard from './fashion/FashionProductCard';
import FashionFilterSheet from './fashion/FashionFilterSheet';
import FashionProductScreen from './FashionProductScreen';
import ProductRail from './fashion/ProductRail';
import { getRecentlyViewed } from '../utils/recentlyViewed';
import apiService from '../apiService';

const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended',       sort_by: 'order_count', sort_order: 'desc' },
  { value: 'newest',      label: "What's New",         sort_by: 'created_at',  sort_order: 'desc' },
  { value: 'popular',     label: 'Popularity',         sort_by: 'order_count', sort_order: 'desc' },
  { value: 'discount',    label: 'Better Discount',    sort_by: 'discount',    sort_order: 'desc' },
  { value: 'price_asc',   label: 'Price: Low to High', sort_by: 'price',       sort_order: 'asc'  },
  { value: 'price_desc',  label: 'Price: High to Low', sort_by: 'price',       sort_order: 'desc' },
  { value: 'rating',      label: 'Customer Rating',    sort_by: 'rating',      sort_order: 'desc' },
];

const PER_PAGE = 24;

const EMPTY_FILTERS = {
  color: [], size: [], brand: [], min_price: null, max_price: null, in_stock: false,
};

const FashionCategoryScreen = ({
  category,
  onBack,
  cart = [],
  addToCart,
  updateQuantity,
  getCartCount,
  getCartTotal,
  openCheckout,
  toggleFavorite,
  favorites = [],
  showToast,
  appTheme,
  isDesktop = false,
  headerSlot = null,
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recommended');
  const [sortOpen, setSortOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [facets, setFacets] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showMobileSort, setShowMobileSort] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [activeSubId, setActiveSubId] = useState(null);   // null = "All"
  const [deals, setDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [recent, setRecent] = useState([]);

  const debounceRef = useRef(null);

  // Which category the grid actually queries: the chosen subcategory, or the
  // parent when "All" is selected.
  const effectiveCategoryId = activeSubId ?? category.id;

  // Subcategories for the drill-down row. The categories endpoint already
  // eager-loads `children`, so the object usually arrives with them; the fetch
  // is a fallback for entry points that pass a thinner category.
  useEffect(() => {
    let cancelled = false;
    setActiveSubId(null);

    const fromProp = Array.isArray(category.children) ? category.children : null;
    if (fromProp) {
      setSubcategories(fromProp.filter((c) => c.is_active !== false));
      return undefined;
    }

    apiService.getCategories({ parent_id: category.id, per_page: 50 }).then((resp) => {
      if (cancelled) return;
      const list = resp?.data?.categories?.data || resp?.data?.categories || [];
      setSubcategories(Array.isArray(list) ? list.filter((c) => c.is_active !== false) : []);
    });

    return () => { cancelled = true; };
  }, [category.id, category.children]);

  const getSortParams = (sortValue) => {
    const opt = SORT_OPTIONS.find((o) => o.value === sortValue) || SORT_OPTIONS[0];
    return { sort_by: opt.sort_by, sort_order: opt.sort_order };
  };

  const activeSortLabel = (SORT_OPTIONS.find((o) => o.value === sortBy) || SORT_OPTIONS[0]).label;

  // The API preserves Laravel's paginator envelope, but older shapes still turn
  // up in feature-section payloads — stay defensive.
  const parseResponse = (resp) => {
    const paginated = resp?.data?.products || resp?.data;
    const items = Array.isArray(paginated?.data) ? paginated.data
      : Array.isArray(paginated) ? paginated
      : Array.isArray(resp) ? resp
      : [];
    return {
      items,
      lastPage: paginated?.last_page || 1,
      total: paginated?.total ?? items.length,
    };
  };

  const requestParams = useCallback((page) => ({
    search: searchQuery,
    ...getSortParams(sortBy),
    per_page: PER_PAGE,
    page,
    color: filters.color,
    size: filters.size,
    brand: filters.brand,
    min_price: filters.min_price,
    max_price: filters.max_price,
    in_stock: filters.in_stock,
  }), [searchQuery, sortBy, filters]);

  const fetchInitial = useCallback(async () => {
    setLoading(true);
    setProducts([]);
    setHasMore(false);

    try {
      const resp = await apiService.getFashionProducts(effectiveCategoryId, requestParams(1));
      const { items, lastPage, total } = parseResponse(resp);
      setProducts(items);
      setTotalProducts(total);
      setHasMore(lastPage > 1);
      setCurrentPage(1);
    } catch {
      setProducts([]);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  }, [effectiveCategoryId, requestParams]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);

    try {
      const nextPage = currentPage + 1;
      const resp = await apiService.getFashionProducts(effectiveCategoryId, requestParams(nextPage));
      const { items, lastPage } = parseResponse(resp);
      setProducts((prev) => [...prev, ...items]);
      setCurrentPage(nextPage);
      setHasMore(nextPage < lastPage);
    } catch {
      // keep current state on error
    } finally {
      setLoadingMore(false);
    }
  };

  // Debounce search, sort and filter changes; always re-fetch from page 1.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchInitial, 400);
    return () => clearTimeout(debounceRef.current);
  }, [fetchInitial]);

  // Facets only depend on the category, so fetch them once.
  useEffect(() => {
    let cancelled = false;

    const loadFacets = async () => {
      const resp = await apiService.getProductFilters({
        category_id: effectiveCategoryId,
        include_children: 1,
      });
      if (!cancelled) setFacets(resp?.data?.filters || null);
    };

    loadFacets();
    return () => { cancelled = true; };
  }, [effectiveCategoryId]);

  // Top deals. Keyed on the category alone, deliberately not on the grid's
  // sort/filter state — the rail is a fixed shelf, not a second view of the
  // shopper's current query, and re-fetching it on every filter tap would make
  // it flicker.
  useEffect(() => {
    let cancelled = false;

    const loadDeals = async () => {
      setDealsLoading(true);

      const inCategory = await apiService.getTopDeals({ categoryId: category.id, limit: 12 });
      if (cancelled) return;

      let rows = inCategory?.data?.products?.data || [];

      // A category with little or no sale stock still deserves a rail, so top
      // it up store-wide rather than showing three lonely cards.
      if (rows.length < 4) {
        const storeWide = await apiService.getTopDeals({ limit: 12 });
        if (cancelled) return;

        const seen = new Set(rows.map((p) => String(p.id)));
        const extra = (storeWide?.data?.products?.data || [])
          .filter((p) => !seen.has(String(p.id)));

        rows = [...rows, ...extra].slice(0, 12);
      }

      setDeals(rows);
      setDealsLoading(false);
    };

    loadDeals();
    return () => { cancelled = true; };
  }, [category.id]);

  // Recently viewed.
  //
  // Keyed on `selectedProduct` rather than mount: FashionProductScreen renders
  // as a child overlay of this screen and calls addRecentlyViewed() when it
  // opens, so localStorage changes underneath a rail that has already
  // rendered. Re-reading when the overlay closes is what keeps the rail honest.
  useEffect(() => {
    if (selectedProduct) return undefined;   // refresh on close, not on open

    let cancelled = false;

    const loadRecent = async () => {
      const ids = getRecentlyViewed().slice(0, 12);
      if (ids.length === 0) {
        setRecent([]);
        return;
      }

      const resp = await apiService.getProductsByIds(ids);
      if (!cancelled) setRecent(resp?.data?.products || []);
    };

    loadRecent();
    return () => { cancelled = true; };
  }, [selectedProduct, category.id]);

  useEffect(() => {
    apiService.trackPageView(`category:${category.id}`, {
      title: category.name,
      kind: 'category',
      category_id: category.id,
    });
  }, [category.id, category.name]);

  const appliedChips = useMemo(() => {
    const chips = [];
    const labelFor = (code, value) => {
      const option = facets?.options?.find((o) => o.code === code);
      return option?.values.find((v) => String(v.value) === String(value))?.label || value;
    };

    filters.color.forEach((v) => chips.push({ key: `color:${v}`, label: labelFor('color', v), group: 'color', value: v }));
    filters.size.forEach((v) => chips.push({ key: `size:${v}`, label: `Size: ${labelFor('size', v)}`, group: 'size', value: v }));
    filters.brand.forEach((v) => chips.push({ key: `brand:${v}`, label: v, group: 'brand', value: v }));

    if (filters.min_price != null || filters.max_price != null) {
      chips.push({
        key: 'price',
        label: `₹${filters.min_price ?? 0} - ₹${filters.max_price ?? '∞'}`,
        group: 'price',
      });
    }

    if (filters.in_stock) chips.push({ key: 'in_stock', label: 'In stock', group: 'in_stock' });

    return chips;
  }, [filters, facets]);

  const removeChip = (chip) => {
    setFilters((current) => {
      if (chip.group === 'price') return { ...current, min_price: null, max_price: null };
      if (chip.group === 'in_stock') return { ...current, in_stock: false };
      return { ...current, [chip.group]: current[chip.group].filter((v) => v !== chip.value) };
    });
  };

  const skeleton = (
    <div className={`grid ${isDesktop ? 'grid-cols-4 gap-x-5 gap-y-8' : 'grid-cols-2 gap-x-2 gap-y-5'}`}>
      {Array.from({ length: isDesktop ? 8 : 4 }).map((_, i) => (
        <div key={i}>
          <div className="w-full bg-[#f5f5f6] animate-pulse" style={{ aspectRatio: '3 / 4' }} />
          <div className="px-2 pt-2 space-y-2">
            <div className="h-4 bg-[#f5f5f6] w-2/3 animate-pulse" />
            <div className="h-3 bg-[#f5f5f6] w-full animate-pulse" />
            <div className="h-4 bg-[#f5f5f6] w-1/2 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );

  const grid = (
    <>
      {loading && skeleton}

      {!loading && products.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <span className="text-5xl mb-4">🧥</span>
          <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--fx-ink)' }}>
            Oops! No results found
          </p>
          <p className="mt-1" style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>
            Try adjusting your filters or search
          </p>
          {appliedChips.length > 0 && (
            <button
              type="button"
              onClick={() => setFilters(EMPTY_FILTERS)}
              className="fx-btn fx-btn-primary mt-6 px-8"
            >
              Clear all filters
            </button>
          )}
        </div>
      )}

      {!loading && products.length > 0 && (
        <div className={`grid ${isDesktop ? 'grid-cols-4 gap-x-5 gap-y-8' : 'grid-cols-2 gap-x-2 gap-y-5'}`}>
          {products.map((product, index) => (
            <FashionProductCard
              key={`${product.id}-${index}`}
              product={product}
              index={index}
              toggleFavorite={toggleFavorite}
              favorites={favorites}
              isDesktop={isDesktop}
              onOpen={setSelectedProduct}
            />
          ))}
        </div>
      )}

      {!loading && hasMore && (
        <button
          type="button"
          onClick={loadMore}
          disabled={loadingMore}
          className={`fx-btn fx-btn-ghost mt-10 ${isDesktop ? 'block mx-auto px-16' : 'w-full'}`}
        >
          {loadingMore ? 'Loading…' : 'Show more products'}
        </button>
      )}
    </>
  );

  // Rails share the grid's column so the desktop filter sidebar is unaffected.
  // The bleed matches each container's own gutter — the default -mx-4 would
  // sit a unit proud of the grid on mobile and overhang the sidebar on desktop.
  const railBleed = isDesktop ? '' : '-mx-3 px-3';

  const dealsRail = (
    <ProductRail
      variant="deal"
      title="Top Deals"
      subtitle={deals.length > 0 ? `${deals.length} offers` : null}
      products={deals}
      loading={dealsLoading && deals.length === 0}
      onOpen={setSelectedProduct}
      toggleFavorite={toggleFavorite}
      favorites={favorites}
      isDesktop={isDesktop}
      bleedClass={railBleed}
      className="mb-8"
    />
  );

  const recentRail = (
    <ProductRail
      title="Recently Viewed"
      products={recent}
      onOpen={setSelectedProduct}
      toggleFavorite={toggleFavorite}
      favorites={favorites}
      isDesktop={isDesktop}
      bleedClass={railBleed}
    />
  );

  // Subcategory drill-down. Rendered only when the category actually has
  // children, so a leaf category does not show a lone "All" chip.
  const subcategoryRow = subcategories.length > 0 && (
    <div className="flex gap-2 overflow-x-auto hide-scrollbar px-4 pb-3">
      {[{ id: null, name: 'All' }, ...subcategories].map((sub) => {
        const active = String(sub.id) === String(activeSubId);

        return (
          <button
            key={sub.id ?? 'all'}
            type="button"
            onClick={() => setActiveSubId(sub.id)}
            className="px-3 py-1.5 whitespace-nowrap flex-shrink-0"
            style={{
              border: `1px solid ${active ? 'var(--fx-pink)' : 'var(--fx-line)'}`,
              borderRadius: 2,
              fontSize: 13,
              fontWeight: active ? 700 : 400,
              color: active ? 'var(--fx-pink)' : 'var(--fx-ink-2)',
              background: active ? '#fff8fa' : '#fff',
            }}
          >
            {sub.name}
          </button>
        );
      })}
    </div>
  );

  const sortDropdown = (
    <div className="relative">
      <button
        type="button"
        onClick={() => setSortOpen(!sortOpen)}
        onBlur={() => setTimeout(() => setSortOpen(false), 150)}
        className="flex items-center justify-between gap-3 px-3 py-2 bg-white"
        style={{ border: '1px solid var(--fx-line)', borderRadius: 2, minWidth: 230, fontSize: 14 }}
      >
        <span>
          <span style={{ color: 'var(--fx-ink-3)' }}>Sort by : </span>
          <span style={{ fontWeight: 700 }}>{activeSortLabel}</span>
        </span>
        <ChevronDown size={16} style={{ color: 'var(--fx-ink-2)' }} />
      </button>

      {sortOpen && (
        <div
          className="absolute right-0 top-full mt-1 bg-white z-20 py-1"
          style={{ border: '1px solid var(--fx-line)', minWidth: 230, boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}
        >
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onMouseDown={() => { setSortBy(opt.value); setSortOpen(false); }}
              className="w-full text-left px-4 py-2 hover:bg-[#f5f5f6]"
              style={{
                fontSize: 14,
                fontWeight: sortBy === opt.value ? 700 : 400,
                color: sortBy === opt.value ? 'var(--fx-ink)' : 'var(--fx-ink-2)',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="fashion-scope fixed inset-0 z-[220] bg-white flex flex-col">
      {headerSlot}

      {/* ---- Mobile header ---- */}
      {!isDesktop && (
        <div className="flex-shrink-0 bg-white" style={{ borderBottom: '1px solid var(--fx-line)' }}>
          <div className="flex items-center gap-3 px-3 py-3">
            <button type="button" onClick={onBack} className="p-1 -ml-1" aria-label="Go back">
              <ArrowLeft size={22} style={{ color: 'var(--fx-ink)' }} />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="truncate" style={{ fontSize: 15, fontWeight: 700 }}>
                {category.name}
              </h1>
              {!loading && (
                <p style={{ fontSize: 12, color: 'var(--fx-ink-3)' }}>{totalProducts} items</p>
              )}
            </div>
          </div>

          <div className="px-3 pb-3">
            <div className="flex items-center gap-2 px-3 py-2" style={{ background: '#f5f5f6', borderRadius: 2 }}>
              <Search size={15} style={{ color: 'var(--fx-ink-3)' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search in ${category.name}`}
                className="flex-1 bg-transparent outline-none"
                style={{ fontSize: 14, color: 'var(--fx-ink)' }}
              />
              {searchQuery && (
                <button type="button" onClick={() => setSearchQuery('')} aria-label="Clear search">
                  <X size={15} style={{ color: 'var(--fx-ink-3)' }} />
                </button>
              )}
            </div>
          </div>

          {subcategoryRow}

          {appliedChips.length > 0 && (
            <div className="flex gap-2 px-3 pb-3 overflow-x-auto hide-scrollbar">
              {appliedChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => removeChip(chip)}
                  className="flex items-center gap-1.5 px-2.5 py-1 whitespace-nowrap flex-shrink-0"
                  style={{
                    border: '1px solid var(--fx-pink)',
                    borderRadius: 2,
                    fontSize: 12,
                    color: 'var(--fx-pink)',
                  }}
                >
                  {chip.label}
                  <X size={12} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---- Desktop header ---- */}
      {isDesktop && (
        <div className="flex-shrink-0 bg-white px-6" style={{ borderBottom: '1px solid var(--fx-line)' }}>
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center gap-2 pt-4" style={{ fontSize: 13, color: 'var(--fx-ink-3)' }}>
              <button type="button" onClick={onBack} style={{ fontWeight: 700, color: 'var(--fx-ink-2)' }}>
                Home
              </button>
              <span>/</span>
              <span style={{ fontWeight: 700, color: 'var(--fx-ink)' }}>{category.name}</span>
            </div>

            {subcategoryRow}

            <div className="flex items-end justify-between gap-6 py-4">
              <h1 style={{ fontSize: 18, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                {category.name}
                {!loading && (
                  <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--fx-ink-3)', marginLeft: 8 }}>
                    - {totalProducts} items
                  </span>
                )}
              </h1>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-2" style={{ background: '#f5f5f6', borderRadius: 2, width: 260 }}>
                  <Search size={15} style={{ color: 'var(--fx-ink-3)' }} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={`Search in ${category.name}`}
                    className="flex-1 bg-transparent outline-none"
                    style={{ fontSize: 14 }}
                  />
                  {searchQuery && (
                    <button type="button" onClick={() => setSearchQuery('')} aria-label="Clear search">
                      <X size={15} style={{ color: 'var(--fx-ink-3)' }} />
                    </button>
                  )}
                </div>
                {sortDropdown}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- Body ---- */}
      <div className="flex-1 overflow-y-auto">
        {isDesktop ? (
          <div className="max-w-[1400px] mx-auto flex px-6">
            <FashionFilterSheet
              inline
              isDesktop
              facets={facets}
              value={filters}
              onApply={setFilters}
              onClear={() => setFilters(EMPTY_FILTERS)}
            />
            <div className="flex-1 min-w-0 pl-6 py-5">
              {dealsRail}
              {grid}
              {recentRail}
            </div>
          </div>
        ) : (
          <div className="px-3 py-3 pb-20">
            {dealsRail}
            {grid}
            {recentRail}
          </div>
        )}
      </div>

      {/* ---- Mobile bottom bar: Sort | Filter, the marketplace pattern ---- */}
      {!isDesktop && (
        <div
          className="flex-shrink-0 bg-white flex"
          style={{ borderTop: '1px solid var(--fx-line)' }}
        >
          <button
            type="button"
            onClick={() => setShowMobileSort(true)}
            className="flex-1 flex items-center justify-center gap-2 py-3"
            style={{ fontSize: 14, fontWeight: 700 }}
          >
            <ArrowUpDown size={16} />
            Sort
          </button>
          <span style={{ width: 1, background: 'var(--fx-line)' }} />
          <button
            type="button"
            onClick={() => setShowFilters(true)}
            className="flex-1 flex items-center justify-center gap-2 py-3"
            style={{ fontSize: 14, fontWeight: 700 }}
          >
            <SlidersHorizontal size={16} />
            Filter
            {appliedChips.length > 0 && (
              <span
                className="w-4 h-4 rounded-full flex items-center justify-center text-white"
                style={{ background: 'var(--fx-pink)', fontSize: 10 }}
              >
                {appliedChips.length}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Mobile sort sheet */}
      {!isDesktop && showMobileSort && (
        <div className="fixed inset-0 z-[240]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowMobileSort(false)} />
          <div className="absolute inset-x-0 bottom-0 bg-white">
            <p className="fx-label px-4 py-3" style={{ borderBottom: '1px solid var(--fx-line)' }}>
              Sort by
            </p>
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => { setSortBy(opt.value); setShowMobileSort(false); }}
                className="w-full text-left px-4 py-3"
                style={{
                  fontSize: 14,
                  fontWeight: sortBy === opt.value ? 700 : 400,
                  color: sortBy === opt.value ? 'var(--fx-pink)' : 'var(--fx-ink-2)',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Cart nudge, shown only when there is something in the bag */}
      {cart.length > 0 && typeof getCartCount === 'function' && getCartCount() > 0 && !isDesktop && (
        <button
          type="button"
          onClick={openCheckout}
          className="fixed right-3 flex items-center gap-2 px-4 py-2.5 text-white shadow-lg"
          style={{ bottom: 64, background: 'var(--fx-pink)', borderRadius: 4, fontSize: 13, fontWeight: 700 }}
        >
          <ShoppingBag size={16} />
          {getCartCount()} in bag
        </button>
      )}

      {!isDesktop && (
        <FashionFilterSheet
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          isDesktop={false}
          facets={facets}
          value={filters}
          onApply={setFilters}
          onClear={() => setFilters(EMPTY_FILTERS)}
        />
      )}

      {/* Rendered on top of this screen rather than replacing it, so going back
          returns to the grid with filters, sort and scroll position intact. */}
      {selectedProduct && (
        <FashionProductScreen
          getCartCount={getCartCount}
          getCartTotal={getCartTotal}
          openCheckout={openCheckout}
          productId={selectedProduct.slug || selectedProduct.id}
          product={selectedProduct}
          isOpen
          onClose={() => setSelectedProduct(null)}
          addToCart={addToCart}
          cart={cart}
          toggleFavorite={toggleFavorite}
          favorites={favorites}
          showToast={showToast}
          appTheme={appTheme}
          isDesktop={isDesktop}
          headerSlot={headerSlot}
        />
      )}
    </div>
  );
};

export default FashionCategoryScreen;
